import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@lenovo/material/dialog';
import { ModalExportLogComponent } from '../components/modal/modal-export-log/modal-export-log.component';
import { TranslateDefaultValueIfNotFoundPipe } from 'src/app/pipe/translate-default-value-if-not-found/translate-default-value-if-not-found.pipe';
import { CommonExportLogService } from 'src/app/services/export-log/common-export-log.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { SnapshotService } from './snapshot.service';
import { FormatLocaleDateTimePipe } from 'src/app/pipe/format-locale-datetime/format-locale-datetime.pipe';
import { LogType } from 'src/app/enums/export-log.enum';
import { VantageShellService } from 'src/app/services/vantage-shell/vantage-shell.service';

@Injectable({
	providedIn: 'root',
})
export class ExportSnapshotResultsService extends CommonExportLogService {
	constructor(
		private snapshotService: SnapshotService,
		private translate: TranslateDefaultValueIfNotFoundPipe,
		private formatLocaleDateTime: FormatLocaleDateTimePipe,
		dialog: MatDialog,
		http: HttpClient,
		logger: LoggerService,
		shellService: VantageShellService
	) {
		super(dialog, http, logger, shellService);
	}

	public exportSnapshotResults() {
		let statusExport;
		let filePath;

		const exportModal = this.openExportLogComponentsModal(ModalExportLogComponent);

		this.exportLog(LogType.snapshot)
			.then((status) => {
				[statusExport, filePath] = status;
			})
			.catch((error) => {
				this.logger.exception(
					'[SnapshotExportLogComponent] Export Scan Results rejected',
					error
				);
				statusExport = error;
			})
			.finally(() => {
				this.updateExportLogComponentsModal(exportModal, statusExport, filePath);
			});
	}

	/**
	 * Retrieve the css class which should be used in the module icon
	 *
	 * @param moduleId The name of the module
	 */
	private getIconClassFromModuleId(moduleId: string): string {
		switch (moduleId) {
			case 'DisplayDevices':
				return 'display_icon';
			case 'HardDrives':
				return 'hard_drive_icon';
			case 'Keyboards':
				return 'keyboard_icon';
			case 'Memory':
				return 'memory_icon';
			case 'Motherboard':
				return 'motherboard_icon';
			case 'MouseDevices':
				return 'mouse_icon';
			case 'Network':
				return 'network_icon';
			case 'CdRomDrives':
				return 'cd_rom_icon';
			case 'Printers':
				return 'printer_icon';
			case 'Processors':
				return 'processor_icon';
			case 'SoundCards':
				return 'sound_card_icon';
			case 'VideoCards':
				return 'video_card_icon';
			case 'Programs':
				return 'program_icon';
			case 'OperatingSystems':
				return 'operating_system_icon';
			case 'StartupPrograms':
				return 'startup_program_icon';
			case 'WebBrowsers':
				return 'web_browser_icon';
			default:
				return 'processor_icon';
		}
	}

	private async prepareDataFromScanLog(): Promise<any> {
		const preparedData: any = {};
		preparedData.hardwareComponents = [];
		preparedData.softwareComponents = [];

		preparedData.model = {
			machineModel: this.machineModel,
			serialNumber: this.serialNumber,
			biosVersion: this.biosVersion,
			productName: this.productName,
		};

		preparedData.environment = {
			experienceVersion: this.experienceVersion,
			shellVersion: this.shellVersion,
			bridgeVersion: this.bridgeVersion,
			addinVersion: await this.snapshotService.addinVersion,
		};

		this.snapshotService.getSoftwareComponentsList().forEach((key) => {
			preparedData.softwareComponents.push({
				name: key,
				content: this.snapshotService.snapshotInfo[key],
			});
		});

		this.snapshotService.getHardwareComponentsList().forEach((key) => {
			preparedData.hardwareComponents.push({
				name: key,
				content: this.snapshotService.snapshotInfo[key],
			});
		});

		return preparedData;
	}

	private populateTemplateModelSection(data: any, modelItemsOrder: Array<string>) {
		let grayItem = true;

		const spanModel = this.document.getElementById('model');
		const machineModel = this.document.getElementById('machine_model');
		const machineInfoItems = this.document.getElementById('machine_info_items');

		spanModel.innerHTML = this.translate.transform('snapshot.report.modelTitle');
		machineModel.innerHTML = data.model.machineModel;

		for (const key of modelItemsOrder) {
			const value = data.model[key];
			const div = this.createItemDiv(
				this.translate.transform('snapshot.report.model.' + key),
				value,
				grayItem
			);
			machineInfoItems.appendChild(div);
			grayItem = !grayItem;
		}
	}

	private populateTemplateEnvironmentSection(
		data: any,
		environmentItemsOrder: Array<string>
	): void {
		let grayItem = true;

		const environmentSectionTitle = this.document.getElementById('environment_title');
		const environmentInfoSectionItems = this.document.getElementById('environment_info_items');
		environmentSectionTitle.innerHTML = this.translate.transform(
			'snapshot.report.environmentTitle'
		);

		for (const key of environmentItemsOrder) {
			const value = data.environment[key];
			const div = this.createItemDiv(
				this.translate.transform('snapshot.report.environment.' + key, key),
				value,
				grayItem
			);
			environmentInfoSectionItems.appendChild(div);
			grayItem = !grayItem;
		}
	}

	private populateTemplateModulesListSection(data: any) {
		let count = 0;
		const hardwareList = this.document.getElementById('modules_hardware_components');
		const hardwareListTitle = this.document.getElementById('title_hardware_components');
		const softwareList = this.document.getElementById('modules_software_components');
		const softwareListTitle = this.document.getElementById('title_software_components');

		hardwareListTitle.innerHTML = this.translate.transform('snapshot.hardwareListTitle');
		softwareListTitle.innerHTML = this.translate.transform('snapshot.softwareListTitle');

		for (const item of data.hardwareComponents) {
			if (item.content.info.Items.length > 0) {
				const module = this.createModuleDiv(item, count);
				hardwareList.appendChild(module);
				count++;
			}
		}

		count = 0;
		for (const item of data.softwareComponents) {
			if (item.content.info.Items.length > 0) {
				const module = this.createModuleDiv(item, count);
				softwareList.appendChild(module);
				count++;
			}
		}
	}

	/**
	 * Used to create a details block style div, applying the right css style.
	 *
	 * @param name The value of the left span inside the div.
	 * @param value The value of the right/center span inside the div.
	 * @param secondValue The value of the right inside the div.
	 * @param gray A boolean indicating whether the div background should be gray or not.
	 */
	private createItemDivDetails(
		name: string,
		value: string,
		secondValue: string,
		gray = false,
		isDifferent = false
	) {
		let itemSecondValue;

		// If name is empty, change the css to especify the details title.
		const classeProperty = name ? 'item_value' : 'details_light_blue_title';

		// Create elements to append in the div
		const itemName = this.createElement({
			elementType: 'span',
			innerHtml: name ?? '',
			classes: ['font_weight_600', 'capitalize_text', 'item_description'],
		});
		const itemValue = this.createElement({
			elementType: 'span',
			innerHtml: value ?? '',
			classes: [classeProperty],
		});

		if (!isDifferent) {
			itemSecondValue = this.createElement({
				elementType: 'span',
				innerHtml: secondValue ?? '',
				classes: [classeProperty],
			});
		} else {
			itemSecondValue = this.createElement({
				elementType: 'span',
				innerHtml: secondValue ?? '',
				classes: ['details_light_blue_title', 'font_weight_600'],
			});
		}

		// Create the div main element
		const div = this.createElement({
			elementType: 'div',
			innerHtml: undefined,
			classes: ['item'],
		});

		// Append the previous elements in the div
		div.appendChild(itemName);
		div.appendChild(itemValue);
		div.appendChild(itemSecondValue);

		// Defines the div background
		if (gray) {
			div.classList.add('bg_gray');
		}

		return div;
	}

	/**
	 * Helper function to create a "key: value" style div, applying the right css style.
	 *
	 * @param name The value of the left span inside the div.
	 * @param value The value of the right/center span inside the div.
	 * @param gray A boolean indicating whether the div background should be gray or not
	 */
	private createItemDiv(name: string, value: string, gray = false): Element {
		// Create elements to append in the div
		const itemName = this.createElement({
			elementType: 'span',
			innerHtml: name,
			classes: ['font_weight_600', 'capitalize_text', 'item_description'],
		});
		const itemValue = this.createElement({
			elementType: 'span',
			innerHtml: value,
			classes: ['item_value'],
		});

		// Create the div main element
		const div = this.createElement({
			elementType: 'div',
			innerHtml: undefined,
			classes: ['item'],
		});

		// Append the previous elements in the div
		div.appendChild(itemName);
		div.appendChild(itemValue);

		// Defines the div background
		if (gray) {
			div.classList.add('bg_gray');
		}

		return div;
	}

	/**
	 * Helper function to create an html element, filling its innerHTML attribute and applying a css style
	 *
	 * @param elementType A string representing the element's type name (e.g. "div")
	 * @param innerHtml The value that should be used as element's innerHTML
	 * @param classes An array containing the css styles to be applied in the new element
	 */
	private createElement({
		elementType,
		innerHtml,
		classes,
	}: {
		elementType: any;
		innerHtml?: any;
		classes?: any;
	}): Element {
		const element = this.document.createElement(elementType);

		if (innerHtml !== undefined) {
			element.innerHTML = innerHtml;
		}

		if (classes !== undefined) {
			for (const c of classes) {
				element.classList.add(c);
			}
		}

		return element;
	}

	/**
	 * Helper function to create a div representing a whole module
	 *
	 * @param item A object containing the module's information
	 * @param moduleCount A number to be used as identify to the module
	 */
	private createModuleDiv(item: any, moduleCount: number): Element {
		let grayItem = true;

		// Module Section Title and icon
		const spanModuleTitle = this.createElement({
			elementType: 'span',
			innerHtml: this.translate.transform('snapshot.components.' + item.name, item.name),
			classes: ['font_weight_600', 'capitalize_text', 'section_title'],
		});
		const divModuleIcon = this.createElement({
			elementType: 'div',
			innerHtml: undefined,
			classes: ['module_icon', this.getIconClassFromModuleId(item.name)],
		});
		const divModuleTitle = this.createElement({
			elementType: 'div',
			innerHtml: undefined,
			classes: ['content_title', 'center_content'],
		});
		divModuleTitle.id = item.name.toLowerCase() + '-' + moduleCount;
		divModuleTitle.appendChild(divModuleIcon);
		divModuleTitle.appendChild(spanModuleTitle);

		// Separator
		const hrSeparator = this.document.createElement('hr');

		// Details titles
		const titleDetailsModule = this.createItemDivDetails(
			'',
			this.translate.transform('snapshot.baselineSnapshot'),
			this.translate.transform('snapshot.lastSnapshot')
		);

		// Details div items
		const divModuleDetails = this.document.createElement('div');

		// Create update line module information
		const updateDateModule = this.createItemDivDetails(
			this.translate.transform('snapshot.properties.UpdatedDate'),
			this.formatLocaleDateTime.transform(item.content.info.BaselineDate + ' UTC'),
			this.formatLocaleDateTime.transform(item.content.info.LastSnapshotDate + ' UTC'),
			grayItem
		);

		divModuleDetails.appendChild(hrSeparator);
		divModuleDetails.appendChild(titleDetailsModule);
		divModuleDetails.appendChild(updateDateModule);

		// Create the details lines to properties
		for (const device of item.content.info.Items) {
			for (const property of device.Properties) {
				grayItem = !grayItem;

				const divProperty = this.createItemDivDetails(
					this.translate.transform(
						'snapshot.properties.' + property.PropertyName,
						property.PropertyName
					),
					this.translate.transform(
						'snapshot.propertyValue.' + property.BaseValue,
						property.BaseValue
					),
					this.translate.transform(
						'snapshot.propertyValue.' + property.CurrentValue,
						property.CurrentValue
					),
					grayItem,
					property.IsDifferent
				);

				divModuleDetails.appendChild(divProperty);
			}

			// Create a separator between properties
			grayItem = false;
			let divPropertyItem = this.createItemDiv('', '', grayItem);
			divModuleDetails.appendChild(divPropertyItem);

			if (device.SubDevices != null) {
				for (const subDevice of device.SubDevices) {
					for (const property of subDevice.Properties) {
						grayItem = !grayItem;

						const divProperty = this.createItemDivDetails(
							this.translate.transform(
								'snapshot.properties.' + property.PropertyName,
								property.PropertyName
							),
							this.translate.transform(
								'snapshot.propertyValue.' + property.BaseValue,
								property.BaseValue
							),
							this.translate.transform(
								'snapshot.propertyValue.' + property.CurrentValue,
								property.CurrentValue
							),
							grayItem,
							property.IsDifferent
						);
						divModuleDetails.appendChild(divProperty);
					}

					// Create a separator between properties
					grayItem = false;
					divPropertyItem = this.createItemDiv('', '', grayItem);
					divModuleDetails.appendChild(divPropertyItem);
				}
			}
		}

		// Whole module div
		const divModule = this.createElement({
			elementType: 'div',
			innerHtml: undefined,
			classes: ['section'],
		});
		divModule.classList.add('module');
		divModule.appendChild(divModuleTitle);
		divModule.appendChild(divModuleDetails);

		return divModule;
	}

	protected populateHtml(jsonData: any) {
		let environmentInfoItemsOrder = [];
		let modelItemsOrder = [];
		const fileTitle = this.document.getElementById('title');

		fileTitle.innerHTML = this.translate.transform('snapshot.title');

		modelItemsOrder = ['productName', 'serialNumber', 'biosVersion'];
		environmentInfoItemsOrder = [
			'experienceVersion',
			'shellVersion',
			'bridgeVersion',
			'addinVersion',
		];

		// Model Section
		this.populateTemplateModelSection(jsonData, modelItemsOrder);

		// Environment Section
		this.populateTemplateEnvironmentSection(jsonData, environmentInfoItemsOrder);

		// Hardware and Software List Section
		this.populateTemplateModulesListSection(jsonData);
	}

	protected prepareData(): Promise<any> {
		return this.prepareDataFromScanLog();
	}
}
